import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
	ApiError,
	type AttendanceEntry,
	type CastMe,
	deleteAttendance,
	fetchMe,
	getAccessToken,
	listMyShifts,
	submitAttendance,
	updateAttendance,
} from "@/lib/api.ts";

type State =
	| { kind: "loading" }
	| { kind: "ready"; cast: CastMe; shifts: AttendanceEntry[] }
	| { kind: "error"; message: string };

export function Shifts() {
	const [, setLocation] = useLocation();
	const [state, setState] = useState<State>({ kind: "loading" });
	const [date, setDate] = useState(nextWorkdayISO());
	const [startTime, setStartTime] = useState("19:00");
	const [endTime, setEndTime] = useState("24:00");
	const [submitting, setSubmitting] = useState(false);
	const [flash, setFlash] = useState<string | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editStart, setEditStart] = useState("");
	const [editEnd, setEditEnd] = useState("");
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const reloadShifts = useCallback(async (castId: string): Promise<AttendanceEntry[]> => {
		return listMyShifts(castId);
	}, []);

	useEffect(() => {
		if (!getAccessToken()) {
			setLocation("/");
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const cast = await fetchMe();
				const shifts = await reloadShifts(cast.id);
				if (!cancelled) setState({ kind: "ready", cast, shifts });
			} catch (err) {
				if (cancelled) return;
				if (err instanceof ApiError && err.status === 401) {
					setLocation("/");
					return;
				}
				setState({
					kind: "error",
					message: err instanceof Error ? err.message : "予期しないエラーが発生しました",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [setLocation, reloadShifts]);

	if (state.kind === "loading") {
		return (
			<main className="flex min-h-screen items-center justify-center">
				<p className="text-sm text-gray-600">読み込み中...</p>
			</main>
		);
	}

	if (state.kind === "error") {
		return (
			<main className="mx-auto max-w-md px-4 py-16">
				<div className="rounded-2xl bg-white p-6 shadow-sm">
					<h1 className="text-lg font-semibold">エラー</h1>
					<p className="mt-3 text-sm text-gray-700">{state.message}</p>
				</div>
			</main>
		);
	}

	function flashOnce(message: string) {
		setFlash(message);
		setTimeout(() => setFlash(null), 2000);
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (state.kind !== "ready") return;
		setSubmitting(true);
		try {
			await submitAttendance([{ date, startTime, endTime }]);
			const shifts = await reloadShifts(state.cast.id);
			setState({ kind: "ready", cast: state.cast, shifts });
			flashOnce("提出しました");
		} catch (err) {
			setState({
				kind: "error",
				message: err instanceof Error ? err.message : "提出に失敗しました",
			});
		} finally {
			setSubmitting(false);
		}
	}

	function startEdit(shift: AttendanceEntry) {
		setDeletingId(null);
		setEditingId(shift.id);
		setEditStart(shift.startTime);
		setEditEnd(shift.endTime);
	}

	function cancelEdit() {
		setEditingId(null);
	}

	async function saveEdit(shiftId: string) {
		if (state.kind !== "ready") return;
		try {
			await updateAttendance(shiftId, { startTime: editStart, endTime: editEnd });
			const shifts = await reloadShifts(state.cast.id);
			setState({ kind: "ready", cast: state.cast, shifts });
			setEditingId(null);
			flashOnce("更新しました");
		} catch (err) {
			flashOnce(err instanceof Error ? err.message : "更新に失敗しました");
		}
	}

	// Two-step inline confirmation — window.confirm() is a no-op inside the
	// LIFF in-client browser, which does not support blocking dialogs (#11).
	function requestDelete(shift: AttendanceEntry) {
		setEditingId(null);
		setDeletingId(shift.id);
	}

	function cancelDelete() {
		setDeletingId(null);
	}

	async function confirmDelete(shift: AttendanceEntry) {
		if (state.kind !== "ready") return;
		try {
			await deleteAttendance(shift.id);
			const shifts = await reloadShifts(state.cast.id);
			setState({ kind: "ready", cast: state.cast, shifts });
			flashOnce("削除しました");
		} catch (err) {
			flashOnce(err instanceof Error ? err.message : "削除に失敗しました");
		} finally {
			setDeletingId(null);
		}
	}

	return (
		<main className="mx-auto max-w-md space-y-4 px-4 py-8">
			<header className="flex items-center justify-between">
				<div>
					<p className="text-xs text-gray-500">こんにちは</p>
					<h1 className="text-xl font-semibold">{state.cast.name} さん</h1>
				</div>
				<div className="flex gap-3 text-xs text-gray-600 underline underline-offset-2">
					<Link href="/diary">日記</Link>
					<Link href="/profile">プロフィール</Link>
				</div>
			</header>

			<form onSubmit={onSubmit} className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
				<h2 className="text-base font-semibold">出勤予定を提出</h2>

				<label className="block">
					<span className="text-xs text-gray-600">日付</span>
					<input
						type="date"
						value={date}
						onChange={(e) => setDate(e.target.value)}
						className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						required
					/>
				</label>

				<div className="flex gap-3">
					<label className="flex-1">
						<span className="text-xs text-gray-600">開始</span>
						<input
							type="time"
							value={startTime}
							onChange={(e) => setStartTime(e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
							required
						/>
					</label>
					<label className="flex-1">
						<span className="text-xs text-gray-600">終了</span>
						<input
							type="time"
							value={endTime}
							onChange={(e) => setEndTime(e.target.value)}
							className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
							required
						/>
					</label>
				</div>

				<button
					type="submit"
					disabled={submitting}
					className="w-full rounded-xl bg-green-600 py-3 text-sm font-semibold text-white disabled:opacity-50 active:bg-green-700"
				>
					{submitting ? "送信中..." : "提出する"}
				</button>

				{flash ? <p className="text-center text-xs text-green-700">{flash}</p> : null}
			</form>

			<section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
				<h2 className="text-base font-semibold">今後のシフト</h2>
				{state.shifts.length === 0 ? (
					<p className="text-xs text-gray-500">まだ提出されたシフトはありません。</p>
				) : (
					<ul className="space-y-2">
						{state.shifts.map((shift) => (
							<li
								key={shift.id}
								className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 text-sm"
							>
								{editingId === shift.id ? (
									<div className="space-y-2">
										<p className="text-xs text-gray-500">{shift.date}</p>
										<div className="flex gap-2">
											<input
												type="time"
												value={editStart}
												onChange={(e) => setEditStart(e.target.value)}
												className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
											/>
											<input
												type="time"
												value={editEnd}
												onChange={(e) => setEditEnd(e.target.value)}
												className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
											/>
										</div>
										<div className="flex gap-2">
											<button
												type="button"
												onClick={cancelEdit}
												className="flex-1 rounded-lg border border-gray-200 py-1.5 text-xs text-gray-600"
											>
												キャンセル
											</button>
											<button
												type="button"
												onClick={() => saveEdit(shift.id)}
												className="flex-1 rounded-lg bg-green-600 py-1.5 text-xs font-semibold text-white"
											>
												保存
											</button>
										</div>
									</div>
								) : deletingId === shift.id ? (
									<div className="space-y-2">
										<p className="text-xs text-gray-700">{shift.date} のシフトを削除しますか?</p>
										<div className="flex gap-2">
											<button
												type="button"
												onClick={cancelDelete}
												className="flex-1 rounded-lg border border-gray-200 py-1.5 text-xs text-gray-600"
											>
												キャンセル
											</button>
											<button
												type="button"
												onClick={() => confirmDelete(shift)}
												className="flex-1 rounded-lg bg-red-600 py-1.5 text-xs font-semibold text-white"
											>
												削除する
											</button>
										</div>
									</div>
								) : (
									<div className="flex items-center justify-between gap-3">
										<div>
											<p className="font-medium">{shift.date}</p>
											<p className="text-xs text-gray-500">
												{shift.startTime}〜{shift.endTime}
												{shift.isConfirmed ? " · 確定" : ""}
											</p>
										</div>
										<div className="flex gap-2">
											<button
												type="button"
												onClick={() => startEdit(shift)}
												className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
											>
												編集
											</button>
											<button
												type="button"
												onClick={() => requestDelete(shift)}
												className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
											>
												削除
											</button>
										</div>
									</div>
								)}
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

function nextWorkdayISO(): string {
	const d = new Date();
	d.setDate(d.getDate() + 1);
	return d.toISOString().slice(0, 10);
}
